import { ReactNode, useEffect, useState } from 'react';
import { useBeforeunload } from 'react-beforeunload';

import { match } from 'ts-pattern';

import { useAgentContext } from 'lib/common/contexts/AgentContext';

import { useHistory } from 'lib/common/hooks/useHistory';

import Loader from 'lib/common/components/Loader';
import { LogEvents, logger } from 'lib/common/components/LoggerController';

import connectGetter from 'lib/common/utils/connectGetter';
import getQueryParam from 'lib/common/utils/getQueryParam';

import { useStreamsContext } from '../StreamsProvider';
import Context from './Context';
import AuthScreen from './components/AuthScreen';
import LoginPage from './components/Login';
import SigningOutOverlay from './components/SigningOutOverlay';
import { useAmplify } from './hooks/useAmplify';
import { useAuthFlow } from './hooks/useAuthFlow';
import { useAuthSession } from './hooks/useAuthSession';
import { useConnectUser } from './hooks/useConnectUser';
import { useForgetDevice } from './hooks/useForgetDevice';
import { useInitializeUser } from './hooks/useInitializeUser';

type Props = {
  children: ReactNode;
  isIsolatedMode?: boolean;
};

const AuthProvider = ({ children, isIsolatedMode }: Props) => {
  const { agent } = useAgentContext();
  const { failed: ccpFailed } = useStreamsContext();

  const [email, setEmail] = useState<string>('');

  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const [isolatedModeToken] = useState<undefined | string>(getQueryParam({ param: 'token' }));
  const [isolatedModeUsername] = useState<undefined | string>(getQueryParam({ param: 'user' }));

  const isolatedModeDetails =
    isolatedModeToken && isolatedModeUsername
      ? { token: isolatedModeToken, connectUsername: isolatedModeUsername }
      : undefined;

  const { fetch_, signedOut, setSignedOut } = useAuthSession();

  const {
    authStage,
    invalidCode,
    mfaDestinations,
    mfaSelection,
    signingOutRef,
    signingOut,
    signIn,
    signOut,
    resendCode,
    restartAuthFlow,
    selectMfaDestination,
    provideMfaCode,
    clearInvalidCode,
  } = useAuthFlow({
    setError,
    email,
    fetch_,
    setSignedOut,
    isolatedModeDetails,
  });

  const { connectUserId } = useConnectUser({ loaded, signIn, setError });

  const { isAmplifyConfigured } = useAmplify();

  const { deviceForgotten, forgetDevice } = useForgetDevice();

  const { user, getUser, userPreferences } = useInitializeUser({
    authStage,
    fetch_,
    setError,
    signOut,
    setEmail,
    setLoaded,
    connectUserId,
  });

  const { navigate } = useHistory();
  useEffect(() => {
    if (!isolatedModeDetails) {
      return;
    }

    navigate(location.pathname, { replace: true, queryParams: { standalone: 'true' }, forceClearQuery: true });
  }, [isolatedModeToken]);

  useEffect(() => {
    if (isIsolatedMode) {
      setLoaded(true);
    }
  }, [authStage]);

  useEffect(() => {
    if (!isIsolatedMode) {
      return;
    }

    signIn();
  }, [isAmplifyConfigured]);

  useBeforeunload((event) => {
    const contacts = connectGetter(agent, 'getContacts') || [];

    // If there are no contacts, or we are signing out, don't block reload
    if (!contacts.length || signingOutRef.current) {
      return;
    }

    // Show dialog to prevent users from refreshing the page when they have tasks
    logger.warn(LogEvents.PAGE_RELOAD_WITH_TASKS, { contacts });
    event.preventDefault();
  });

  return (
    <Context.Provider
      value={{
        fetch_,
        loaded,
        signOut,
        connectUserId,
        user,
        userPreferences,
        getUser,
        forgetDevice,
      }}
    >
      {signingOut && signingOutRef?.current && <SigningOutOverlay type={signingOutRef?.current} />}

      {/* if this gets any larger, break it out into a new component and move all the loginPage props to context */}
      {match({
        error,
        ccpFailed,
        signedOut,
        user: Boolean(user),
        loaded,
        deviceForgotten,
        isIsolatedMode,
        signOutType: signingOutRef?.current,
      })
        .with({ user: false, isIsolatedMode: true }, () => <Loader fixed />)
        .with({ deviceForgotten: true }, () => <AuthScreen type="DEVICE_FORGOTTEN" />)
        .with({ signedOut: true, signOutType: 'AUTH_FAIL' }, () => <AuthScreen type="AUTH_FAIL_SIGNED_OUT" />)
        .with({ signedOut: true }, () => <AuthScreen type="SIGNED_OUT" />)
        .with({ error: true }, { ccpFailed: true }, () => <AuthScreen type="AUTH_ERROR" />)
        .with({ user: false }, () => (
          <LoginPage
            authStage={authStage}
            mfaDestinations={mfaDestinations}
            mfaSelection={mfaSelection}
            invalidCode={invalidCode}
            selectMfaDestination={selectMfaDestination}
            provideMfaCode={provideMfaCode}
            resendCode={resendCode}
            restartAuthFlow={restartAuthFlow}
            clearInvalidCode={clearInvalidCode}
          />
        ))
        .with({ user: true, loaded: false }, () => <Loader fixed />)
        .with({ loaded: true }, () => children)
        .exhaustive()}
    </Context.Provider>
  );
};

export default AuthProvider;
